import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Printer, ArrowLeft, Package } from "lucide-react";
import { useApi, useAdminSession, SITE, fmtUSD, fmtDay, fmtDate } from "@/lib/api";

const Line = ({ k, v, bold, green }) => (
  <div className={`flex justify-between py-1.5 text-sm ${bold ? "font-bold text-base" : ""} ${green ? "text-emerald-700" : ""}`}><span>{k}</span><span className="font-mono">{v}</span></div>
);

function Doc({ inv, receipt }) {
  const sm = inv.summary;
  return (
    <div className="invoice-sheet bg-white border border-slate-200 rounded-sm p-6 sm:p-10 space-y-8" data-testid={receipt ? "receipt-doc" : "invoice-doc"}>
      <div className="flex flex-wrap justify-between gap-6">
        <div>
          <p className="flex items-center gap-2 font-display font-extrabold text-xl text-[#0A192F]"><Package className="h-6 w-6 text-[#FF6B00]" /> INLA SHIPPING HUB</p>
          <p className="text-sm text-slate-500 mt-1">{SITE.domain} · {SITE.email} · {SITE.phone}</p>
        </div>
        <div className="sm:text-right">
          <p className="font-display text-2xl font-bold">{receipt ? "PAYMENT RECEIPT" : "INVOICE"}</p>
          <p className="font-mono text-sm" data-testid="doc-number">{receipt ? receipt.receipt_number : sm.invoice_number || "Draft"}</p>
          <p className="text-sm text-slate-500">{receipt ? fmtDate(receipt.paid_at) : fmtDay(inv.invoice_date)}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-6 text-sm">
        <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Bill to</p><p className="font-semibold">{inv.sender.name}</p><p className="text-slate-600">{inv.sender.address}</p>{inv.sender.email && <p className="text-slate-600">{inv.sender.email}</p>}</div>
        <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Deliver to</p><p className="font-semibold">{inv.receiver.name}</p><p className="text-slate-600">{inv.receiver.address}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Shipment</p><p className="font-mono font-semibold">{inv.tracking_number}</p><p className="text-slate-600">{inv.origin} → {inv.destination}</p><p className="text-slate-600">{inv.package.contents} · {inv.package.weight_kg} kg · {inv.package.service}</p></div>
      </div>
      {receipt ? (
        <div className="max-w-sm ml-auto">
          <Line k="Amount received" v={fmtUSD(receipt.amount)} bold />
          <Line k="Method" v={receipt.method} />
          {receipt.reference && <Line k="Reference" v={receipt.reference} />}
          <Line k="Invoice total" v={fmtUSD(sm.total)} />
          <Line k="Total paid to date" v={fmtUSD(sm.paid)} />
          <Line k="Balance remaining" v={fmtUSD(sm.balance)} bold />
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead><tr className="border-b-2 border-[#0A192F] text-left"><th className="py-2">Description</th><th className="py-2 text-right">Amount (USD)</th></tr></thead>
            <tbody>
              {inv.items.map((i) => <tr key={i.id} className="border-b border-slate-100"><td className="py-2">{i.description}</td><td className={`py-2 text-right font-mono ${i.type === "discount" ? "text-emerald-700" : ""}`}>{i.type === "discount" ? "-" : ""}{fmtUSD(i.amount)}</td></tr>)}
              {!inv.items.length && <tr><td colSpan={2} className="py-4 text-slate-500">No charges have been added yet.</td></tr>}
            </tbody>
          </table>
          <div className="max-w-sm ml-auto">
            <Line k="Charges" v={fmtUSD(sm.charges)} />
            {sm.discounts > 0 && <Line k="Discounts" v={`-${fmtUSD(sm.discounts)}`} green />}
            <Line k="Total shipment charges" v={fmtUSD(sm.total)} bold />
            <Line k="Paid" v={fmtUSD(sm.paid)} />
            <Line k="Balance due" v={fmtUSD(sm.balance)} bold />
            <Line k="Payment status" v={sm.status} />
          </div>
          {inv.payments.length > 0 && <div className="text-sm"><p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Payments received</p>
            {inv.payments.map((p) => <p key={p.id} className="text-slate-600">{fmtDay(p.paid_at)} · {fmtUSD(p.amount)} · {p.method} · {p.receipt_number}</p>)}</div>}
        </>
      )}
      <p className="text-xs text-slate-400">All amounts in US Dollars (USD). Thank you for shipping with Inla Shipping Hub.</p>
    </div>
  );
}

function View({ path, back }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const q = useQuery({ queryKey: ["invoice", path, id], queryFn: () => api("get", path(id)), retry: false, staleTime: 0 });
  const receipt = q.data && params.get("receipt") ? q.data.payments.find((p) => p.id === params.get("receipt")) : null;
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <Link to={back(id)} className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0A192F]" data-testid="invoice-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <button onClick={() => window.print()} disabled={!q.data} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF6B00] hover:bg-[#E05D00] text-white font-semibold text-sm rounded-sm disabled:opacity-50" data-testid="invoice-print-btn"><Printer className="h-4 w-4" /> Print / Save PDF</button>
      </div>
      {q.isLoading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
      {q.isError && <p className="text-slate-500" data-testid="invoice-not-found">Invoice not found.</p>}
      {q.data && <Doc inv={q.data} receipt={receipt} />}
    </div>
  );
}

export function AdminInvoicePage() {
  const { isLoading, isError, data } = useAdminSession();
  if (isError) return <Navigate to="/admin/login" replace />;
  if (isLoading || !data) return <div className="p-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return <View path={(id) => `/admin/shipments/${id}/invoice`} back={(id) => `/admin/shipments/${id}`} />;
}

export function CustomerInvoicePage() {
  return <View path={(id) => `/my/shipments/${id}/invoice`} back={() => "/dashboard"} />;
}
